/**
 * Performance Alerts saga.
 *
 * Every saga tries the real API first and falls back to a localStorage store
 * if the request fails (backend endpoint not deployed yet). When the backend
 * lands, the fallback simply stops running — no UI change required.
 *
 * The localStorage shape mirrors the REST shape (array of AlertRule objects)
 * scoped by company id so multi-company test accounts don't collide.
 */
import { all, call, fork, select, put, takeEvery } from "redux-saga/effects";
import { types } from "./types";
import {
  GET_ALERTS,
  CREATE_ALERT,
  UPDATE_ALERT,
  DELETE_ALERT,
  TOGGLE_ALERT,
} from "../../api/performanceAlerts";

const STORAGE_KEY_PREFIX = "gmb.performanceAlerts.v1";

const safeCall = (meta, key, ...args) => {
  if (meta && typeof meta[key] === "function") {
    try {
      meta[key](...args);
    } catch {
      /* swallow — callbacks must not break the saga */
    }
  }
};

/* ---------- local-storage fallback helpers ---------- */

const storageKey = (companyId) =>
  `${STORAGE_KEY_PREFIX}.${companyId || "default"}`;

const readLocal = (companyId) => {
  try {
    const raw = window.localStorage.getItem(storageKey(companyId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeLocal = (companyId, list) => {
  try {
    window.localStorage.setItem(storageKey(companyId), JSON.stringify(list));
  } catch {
    /* ignore quota errors — UI degrades to in-memory state only */
  }
};

// `Date.now()` and `Math.random()` are fine here — the saga runs at user
// interaction time, not during workflow replay.
const generateId = () =>
  // eslint-disable-next-line no-bitwise
  `pa_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`;

const nowIso = () => new Date().toISOString();

const buildRule = (companyId, partial) => ({
  _id: generateId(),
  companyId,
  name: partial?.name || "Untitled alert",
  enabled: partial?.enabled ?? true,
  trigger: partial?.trigger || {
    condition: "below_target",
    kpiId: "any",
    threshold: null,
  },
  recipients: partial?.recipients || { userIds: [], teams: [], workspaces: [] },
  schedule: partial?.schedule || {
    frequency: "daily",
    time: "17:00",
    daysOfWeek: [1, 2, 3, 4, 5],
    timezone: "America/Los_Angeles",
  },
  channels: partial?.channels || { sms: true, inApp: true, email: false },
  message: partial?.message || "",
  // Preserve the workspace an alert was created in (Team room) so the
  // workspace-scoped list can find it. Null = company-wide (Company Setup).
  workspaceId: partial?.workspaceId ?? null,
  createdAt: nowIso(),
  updatedAt: nowIso(),
  lastSentAt: null,
});

const selectCompanyId = (state) =>
  state?.authReducer?.userDetails?.companyId || "";

/* ---------- sagas ---------- */

function* fetchAlerts(action) {
  try {
    const companyId = yield select(selectCompanyId);
    let list = [];
    try {
      const { data } = yield call(GET_ALERTS);
      list = Array.isArray(data?.alerts) ? data.alerts : [];
    } catch {
      list = readLocal(companyId);
    }
    yield put({ type: types.GET_ALERTS_SUCCESS, list });
    safeCall(action.meta, "onSuccess", list);
  } catch (err) {
    const error = err?.message || "Failed to load alerts";
    yield put({ type: types.GET_ALERTS_FAILED, error });
    safeCall(action.meta, "onError", error);
  }
}

function* createAlert(action) {
  try {
    const companyId = yield select(selectCompanyId);
    let alert;
    try {
      const { data } = yield call(CREATE_ALERT, action.payload);
      alert = data?.alert || data;
    } catch {
      alert = buildRule(companyId, action.payload);
      const list = readLocal(companyId);
      writeLocal(companyId, [alert, ...list]);
    }
    yield put({ type: types.CREATE_ALERT_SUCCESS, alert });
    safeCall(action.meta, "onSuccess", alert);
  } catch (err) {
    const error = err?.message || "Failed to create alert";
    yield put({ type: types.CREATE_ALERT_FAILED, error });
    safeCall(action.meta, "onError", error);
  }
}

function* updateAlert(action) {
  const { id, patch } = action.payload;
  try {
    const companyId = yield select(selectCompanyId);
    let alert;
    try {
      const { data } = yield call(UPDATE_ALERT, id, patch);
      alert = data?.alert || data;
    } catch {
      const list = readLocal(companyId);
      const idx = list.findIndex((r) => r._id === id);
      if (idx >= 0) {
        const next = { ...list[idx], ...patch, updatedAt: nowIso() };
        list[idx] = next;
        writeLocal(companyId, list);
        alert = next;
      }
    }
    if (!alert) throw new Error("Alert not found");
    yield put({ type: types.UPDATE_ALERT_SUCCESS, alert });
    safeCall(action.meta, "onSuccess", alert);
  } catch (err) {
    const error = err?.message || "Failed to update alert";
    yield put({ type: types.UPDATE_ALERT_FAILED, error });
    safeCall(action.meta, "onError", error);
  }
}

function* deleteAlert(action) {
  const { id } = action.payload;
  try {
    const companyId = yield select(selectCompanyId);
    try {
      yield call(DELETE_ALERT, id);
    } catch {
      const list = readLocal(companyId).filter((r) => r._id !== id);
      writeLocal(companyId, list);
    }
    yield put({ type: types.DELETE_ALERT_SUCCESS, id });
    safeCall(action.meta, "onSuccess", { id });
  } catch (err) {
    const error = err?.message || "Failed to delete alert";
    yield put({ type: types.DELETE_ALERT_FAILED, error });
    safeCall(action.meta, "onError", error);
  }
}

function* toggleAlert(action) {
  const { id, enabled } = action.payload;
  try {
    const companyId = yield select(selectCompanyId);
    let alert;
    try {
      const { data } = yield call(TOGGLE_ALERT, id, enabled);
      alert = data?.alert || data;
    } catch {
      const list = readLocal(companyId);
      const idx = list.findIndex((r) => r._id === id);
      if (idx >= 0) {
        const next = { ...list[idx], enabled, updatedAt: nowIso() };
        list[idx] = next;
        writeLocal(companyId, list);
        alert = next;
      }
    }
    if (!alert) throw new Error("Alert not found");
    yield put({ type: types.TOGGLE_ALERT_SUCCESS, alert });
    safeCall(action.meta, "onSuccess", alert);
  } catch (err) {
    const error = err?.message || "Failed to toggle alert";
    yield put({ type: types.TOGGLE_ALERT_FAILED, error });
    safeCall(action.meta, "onError", error);
  }
}

function* watchAlerts() {
  yield takeEvery(types.GET_ALERTS_REQUEST, fetchAlerts);
  yield takeEvery(types.CREATE_ALERT_REQUEST, createAlert);
  yield takeEvery(types.UPDATE_ALERT_REQUEST, updateAlert);
  yield takeEvery(types.DELETE_ALERT_REQUEST, deleteAlert);
  yield takeEvery(types.TOGGLE_ALERT_REQUEST, toggleAlert);
}

function* rootSaga() {
  yield all([fork(watchAlerts)]);
}

export default rootSaga;
